/*
 * How the apps read and write their URL search (`parseSearch`/`stringifySearch`
 * of both routers, #4974). Every value is TEXT: the route schemas decide what a
 * value means. TanStack Router's default does `JSON.parse` on every value — and
 * its decoder already turns `1`/`true` into number/boolean before any custom
 * parser runs — so `?rooms=1` arrived as a number, a numeric `?voucher=` or
 * `?q=` was dropped by `z.string()`, and a string like `"1"` was written back
 * quoted (`rooms=%221%22`). Pure: no router import, usable by any layer.
 */

/** The search as the routes read it: text, or a list for a repeated key. */
export type PlainSearch = Record<string, string | string[]>;

/** A JSON string literal: what the old default writer produced for a string
 *  that `JSON.parse` would also read (`"1"`, `"true"`, `"null"`). */
const JSON_STRING_LITERAL = /^"(?:[^"\\]|\\.)*"$/;

/** Links already out (shares, history, bookmarks) carry the old quoting; read
 *  them as the value they meant. Only a quoted value whose content the old
 *  reader would have parsed as JSON is unwrapped — a text that merely starts
 *  and ends with quotes is left alone. */
function unquoteLegacy(raw: string): string {
  if (!JSON_STRING_LITERAL.test(raw)) return raw;
  try {
    const inner: unknown = JSON.parse(raw);
    if (typeof inner !== 'string') return raw;
    JSON.parse(inner);
    return inner;
  } catch {
    return raw;
  }
}

/** `?a=1&b=x&b=y` → `{ a: '1', b: ['x', 'y'] }`. Accepts the leading `?`. */
export function parsePlainSearch(searchStr: string): PlainSearch {
  // Null prototype: a `__proto__` key in the URL must not reach the prototype.
  const search = Object.create(null) as PlainSearch;
  for (const [key, raw] of new URLSearchParams(searchStr)) {
    const value = unquoteLegacy(raw);
    const previous = search[key];
    if (previous === undefined) search[key] = value;
    else if (Array.isArray(previous)) previous.push(value);
    else search[key] = [previous, value];
  }
  return search;
}

/**
 * `{ a: '1', b: ['x', 'y'] }` → `?a=1&b=x&b=y`, or `''` with nothing to write.
 * `null`/`undefined` are omitted; scalars are written as `String(value)`. An
 * object is a programming error — structured state does not belong in the URL
 * — and throws instead of being serialized.
 */
export function stringifyPlainSearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item === null || item === undefined) continue;
      if (typeof item === 'object' || typeof item === 'function') {
        throw new TypeError(`search param "${key}" must be text, got ${typeof item}`);
      }
      params.append(key, String(item as string | number | boolean | bigint | symbol));
    }
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}
